import React, { useEffect, useRef, useState, useId, forwardRef } from 'react';
import TextField from '@mui/material/TextField';
import { useTheme, useMediaQuery } from '@mui/material';
import { useMaps } from './MapsProvider.jsx';
import MobileCleanInput from './MobileCleanInput.jsx';

// New Places Autocomplete Element wrapper. Falls back to plain TextField when API is unavailable.
const GooglePlaceAutocompleteElement = forwardRef(function GooglePlaceAutocompleteElement({ value, onChange, placeholder, id, label, required = false, fullWidth = true, helperText, ariaLabelledby, error = false, size }, ref) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const USE_WEB_COMPONENT = false; // Force fallback for stability
  const autoId = useId();
  const inputId = id || `place-autocomplete-${autoId}`;
  const containerRef = useRef(null);
  const elementRef = useRef(null);
  const inputRef = useRef(null); // for legacy Autocomplete
  const [usingElement, setUsingElement] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [apiError, setApiError] = useState(false);
  const handlersRef = useRef({});
  const pollerRef = useRef(null);
  const lastValueRef = useRef('');
  const onChangeRef = useRef(onChange);
  const isTypingRef = useRef(false);
  const selfUpdateRef = useRef(false);
  const legacyAutocompleteRef = useRef(null);
  const { isLoaded, loadError } = useMaps();
  const isDev = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.MODE !== 'production';

  // Keep the latest onChange without forcing element teardown/rebuild
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Force remove any validation attributes on mobile - MORE AGGRESSIVE
  useEffect(() => {
    if (!isMobile) return;
    
    const interval = setInterval(() => {
      // Find ALL input elements and strip validation
      const allInputs = document.querySelectorAll('input[type="text"], input[placeholder*="address"], input[placeholder*="Address"]');
      allInputs.forEach(input => {
        input.removeAttribute('required');
        input.removeAttribute('aria-required');
        input.removeAttribute('aria-invalid');
        input.setAttribute('novalidate', 'true');
        input.style.boxShadow = 'none';
        input.style.borderColor = '#bdbdbd';
        
        // Remove validation classes
        input.classList.remove('Mui-error', 'error', 'invalid');
        
        // Remove parent validation classes
        const parent = input.closest('.MuiTextField-root, .MuiFormControl-root');
        if (parent) {
          parent.classList.remove('Mui-error', 'error', 'invalid');
        }
      });
    }, 50); // Check every 50ms

    return () => clearInterval(interval);
  }, [isMobile]);

  useEffect(() => {
    let el;
    let cancelled = false;
    const gmAuthHandler = () => {
      setApiError(true);
      setApiReady(false);
      setUsingElement(false);
    };
    window.gm_authFailure = gmAuthHandler;

    (async () => {
      try {
        if (!window.google?.maps?.importLibrary || !USE_WEB_COMPONENT) {
          setApiReady(false);
          setUsingElement(false);
          return;
        }
        const [{ PlaceAutocompleteElement }] = await Promise.all([
          window.google.maps.importLibrary('places')
        ]);
        if (cancelled || !PlaceAutocompleteElement) return;
  el = new PlaceAutocompleteElement();
  el.id = inputId;
        el.setAttribute('inputmode', 'text');
        if (placeholder) el.setAttribute('placeholder', placeholder);
        // Keep initial value in sync
        if (typeof value === 'string' && value) {
          try { el.value = value; } catch {}
        }
        if (ariaLabelledby) {
          try { el.setAttribute('aria-labelledby', ariaLabelledby); } catch {}
        }
        // Utility to normalize element value to a plain string
        const normalize = (val) => {
          if (!val) return '';
          if (typeof val === 'string') return val;
          if (typeof val === 'object') {
            return val.formattedAddress || val.formatted_address || val.description || '';
          }
          return String(val);
        };

        // Fire onChange while user types for live updates
        const handleInput = () => {
          try {
            selfUpdateRef.current = true;
            onChangeRef.current?.(normalize(el.value));
            // Clear the flag shortly after to allow external sync again
            queueMicrotask?.(() => { selfUpdateRef.current = false; });
          } catch {}
        };
        el.addEventListener('input', handleInput);
        el.addEventListener('change', handleInput);
        el.addEventListener('focus', () => { isTypingRef.current = true; });
        el.addEventListener('blur', () => { isTypingRef.current = false; });
        el.addEventListener('compositionstart', () => { isTypingRef.current = true; });
        el.addEventListener('compositionend', () => { isTypingRef.current = false; });

        // Handle place selection events; use getPlace() when available
        const handlePlaceSelect = () => {
          try {
            const p = typeof el.getPlace === 'function' ? el.getPlace() : null;
            const formatted = p?.formattedAddress || p?.formatted_address || normalize(el.value);
            selfUpdateRef.current = true;
            onChangeRef.current?.(formatted);
            queueMicrotask?.(() => { selfUpdateRef.current = false; });
          } catch {
            selfUpdateRef.current = true;
            onChangeRef.current?.(normalize(el.value));
            queueMicrotask?.(() => { selfUpdateRef.current = false; });
          }
        };
        el.addEventListener('gmp-placeselect', handlePlaceSelect);
        el.addEventListener('gmp-placechange', handlePlaceSelect);
    el.addEventListener('gmp-placeautocomplete-placechanged', handlePlaceSelect);
    handlersRef.current = { handleInput, handlePlaceSelect };

        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          // Ensure dropdown isn't clipped and input spans full width
          try {
            containerRef.current.style.position = 'relative';
            containerRef.current.style.zIndex = '2000';
            containerRef.current.style.overflow = 'visible';
          } catch {}
          try {
            el.style.width = '100%';
            el.style.zIndex = '2000';
            el.style.fontSize = '16px'; // Prevent zoom on iOS
            el.style.border = '1px solid #bdbdbd';
            el.style.borderRadius = '4px';
            el.style.padding = '14px';
            el.style.backgroundColor = '#f8fafc';
            el.style.fontFamily = '"Roboto", "Helvetica", "Arial", sans-serif';
            if (error) {
              el.style.borderColor = '#d32f2f';
            }
          } catch {}
          containerRef.current.appendChild(el);
        }
        elementRef.current = el;
        setApiReady(true);
        setApiError(false);
        setUsingElement(true);

        // Start a lightweight poller to detect value changes while typing
        try {
          lastValueRef.current = normalize(el.value);
          if (!pollerRef.current) {
            pollerRef.current = setInterval(() => {
              try {
                const currentVal = normalize(el.value);
                if (currentVal !== lastValueRef.current) {
                  lastValueRef.current = currentVal;
                  // Only emit when not in composition to avoid breaking suggestions
                  if (!isTypingRef.current) {
                    selfUpdateRef.current = true;
                    onChangeRef.current?.(currentVal);
                    queueMicrotask?.(() => { selfUpdateRef.current = false; });
                  }
                }
              } catch {}
            }, 150);
          }
        } catch {}
      } catch (e) {
        setApiError(true);
        setApiReady(false);
        setUsingElement(false);
      }
    })();

    return () => {
      if (window.gm_authFailure === gmAuthHandler) delete window.gm_authFailure;
      if (pollerRef.current) {
        try { clearInterval(pollerRef.current); } catch {}
        pollerRef.current = null;
      }
      try {
        if (el) {
          const { handleInput, handlePlaceSelect } = handlersRef.current || {};
          if (handleInput) {
            el.removeEventListener('input', handleInput);
            el.removeEventListener('change', handleInput);
          }
          if (handlePlaceSelect) {
            el.removeEventListener('gmp-placeselect', handlePlaceSelect);
            el.removeEventListener('gmp-placechange', handlePlaceSelect);
            el.removeEventListener('gmp-placeautocomplete-placechanged', handlePlaceSelect);
          }
          el.removeEventListener('focus', () => { isTypingRef.current = true; });
          el.removeEventListener('blur', () => { isTypingRef.current = false; });
          el.removeEventListener('compositionstart', () => { isTypingRef.current = true; });
          el.removeEventListener('compositionend', () => { isTypingRef.current = false; });
          if (el.remove) el.remove();
        }
      } catch {}
      cancelled = true;
    };
  }, [inputId, placeholder, isLoaded]);

  // Legacy Places Autocomplete on a regular input as a fallback when the web component isn't used
  useEffect(() => {
    if (usingElement) return; // web component path active
    if (!isLoaded) return;
    const inputEl = inputRef.current;
    if (!inputEl) return;
    if (!window.google?.maps?.places?.Autocomplete) {
      if (isDev) console.warn('[PlacesInput] Autocomplete class unavailable, isLoaded:', isLoaded, 'google:', !!window.google);
      return;
    }
    try {
      const ac = new window.google.maps.places.Autocomplete(inputEl, {
        fields: ['formatted_address', 'name', 'geometry'],
        // types: ['geocode'], // relax filtering to allow broader suggestions
      });
      legacyAutocompleteRef.current = ac;
      if (isDev) console.log('[PlacesInput] Initialized legacy Autocomplete for', inputId);
      const listener = ac.addListener('place_changed', () => {
        try {
          const p = ac.getPlace();
          const formatted = p?.formatted_address || p?.name || inputEl.value || '';
          if (isDev) console.log('[PlacesInput] place_changed', formatted, p);
          selfUpdateRef.current = true;
          onChangeRef.current?.(formatted);
          queueMicrotask?.(() => { selfUpdateRef.current = false; });
        } catch {}
      });
      return () => {
        try { window.google.maps.event.removeListener(listener); } catch {}
        try { window.google.maps.event.clearInstanceListeners(ac); } catch {}
        legacyAutocompleteRef.current = null;
      };
    } catch {}
  }, [usingElement, isLoaded]);

  // Keep external value in sync with element if it changes externally
  useEffect(() => {
    const el = elementRef.current;
    if (usingElement && el && typeof value === 'string' && el.value !== value) {
      // Avoid stomping user input while composing/typing
      if (isTypingRef.current || selfUpdateRef.current) return;
      try { el.value = value; } catch {}
    }
  }, [value, usingElement]);

  if (usingElement) {
    // Render the web element container, rely on Google's shadow DOM for UI
    return (
      <div style={{ width: '100%' }}>
        <div ref={containerRef} style={{ 
          width: '100%', 
          position: 'relative', 
          zIndex: 2000, 
          overflow: 'visible',
          minHeight: size === 'small' ? '40px' : '56px'
        }} />
        {error && helperText && (
          <div style={{ 
            color: '#d32f2f', 
            fontSize: '0.75rem', 
            marginTop: '3px', 
            marginLeft: '14px',
            fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
          }}>
            {helperText}
          </div>
        )}
      </div>
    );
  }

  // Fallback input (manual typing remains fully functional)
  // Use clean input on mobile to avoid validation issues
  if (isMobile) {
    return (
      <MobileCleanInput
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        id={inputId}
        ref={ref}
      />
    );
  }

  // Desktop version with TextField
  return (
    <TextField
      placeholder={placeholder}
      label={label}
      value={value}
      onChange={e => onChange(e.target.value)}
      id={inputId}
      inputProps={{ 
        'aria-labelledby': ariaLabelledby || undefined,
        'noValidate': true, // Disable HTML5 validation
        'aria-invalid': false // Force not invalid
      }}
      inputRef={inputRef}
      autoComplete="off"
      required={false} // Force not required
      fullWidth={fullWidth}
      variant="outlined"
      error={false} // Force no error
      size={size}
      helperText="" // Force no helper text
      FormHelperTextProps={{
        style: { display: 'none' } // Hide helper text completely
      }}
      sx={{
        mt: 1,
        background: '#f8fafc',
        borderRadius: 1,
        width: '100%',
        '& .MuiOutlinedInput-root': {
          fontSize: '16px', // Prevent zoom on iOS
          '& fieldset': {
            borderColor: '#bdbdbd', // Force normal border
            borderWidth: 1,
          },
          '&:hover fieldset': {
            borderColor: '#212F45', // Force normal hover
          },
          '&.Mui-focused fieldset': {
            borderColor: '#212F45', // Force normal focus
            borderWidth: 2,
          },
          // Hide any validation indicators on mobile
          '&:invalid': {
            borderColor: '#bdbdbd !important',
            boxShadow: 'none !important',
          },
        },
        '& .MuiOutlinedInput-input': {
          fontSize: '16px', // Prevent zoom on iOS
          padding: { xs: '12px 14px', sm: '14px' },
          // Disable browser validation styling
          '&:invalid': {
            boxShadow: 'none !important',
            borderColor: 'transparent !important',
          },
          '&:required': {
            boxShadow: 'none !important',
          },
        },
        // Hide any helper text or validation messages
        '& .MuiFormHelperText-root': {
          display: 'none !important',
        },
      }}
      ref={ref}
    />
  );
});

export default GooglePlaceAutocompleteElement;
